import { invalidate } from "@react-three/fiber";
import * as THREE from "three";

import { resolveSymbolLibraryAsset } from "../../components/Views/SymbolLibraryTabs";
import { UploadFloorplanBackdropImageDocument } from "../../gql/floorplanBackdrop";
import { FloorplanImageImportFieldsFragment } from "../../gql/graphql";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import {
  BackdropImageType,
  FloorplannerSelectionType,
  isBackdropImageType,
  isRulerLineType,
  isSingleLineType,
  isSymbolType,
  isWallType,
  LineType,
  RulerLineType,
  SingleLineType,
  SquareSymbolType,
  SvgType,
  SymbolType,
  TextType,
  WallType,
} from "../../types/floorplannerTypes";
import { createEdgeConnection } from "../../utils/edgeConnectionHelpers";
import {
  getSelectionBounds,
  isSelectionBoundsItem,
} from "../../utils/selectionBounds";
import {
  getImportedRectangleFallback,
  getImportedSymbolFootprint,
  resolveImportedCatalogRotation,
} from "./importedSymbolGeometry";

export type FloorplanImportDraft = NonNullable<
  FloorplanImageImportFieldsFragment["draft"]
>;

export type FloorplanImportSymbolOverride = {
  libraryKey: string;
  rotation: number;
};

const samePoint = (a: THREE.Vector2, b: THREE.Vector2) =>
  a.distanceToSquared(b) <= 0.001 ** 2;

/** Corner endpoints within this distance are snapped together then connected. */
const CORNER_SNAP_TOLERANCE = 0.008;
const T_JUNCTION_TOLERANCE = 0.012;
type WallEndpoint = "start" | "end";

const getWallEndpoint = (wall: WallType, endpoint: WallEndpoint) =>
  endpoint === "start" ? wall.start : wall.end;

/**
 * If two corner endpoints are within CORNER_SNAP_TOLERANCE, move both to their
 * midpoint so the subsequent edge connection sees an exact shared vertex.
 */
const snapCornerEndpoints = (
  source: WallType,
  sourceEndpoint: WallEndpoint,
  target: WallType,
  targetEndpoint: WallEndpoint,
) => {
  const sourcePoint = getWallEndpoint(source, sourceEndpoint);
  const targetPoint = getWallEndpoint(target, targetEndpoint);
  const distanceSquared = sourcePoint.distanceToSquared(targetPoint);
  if (
    distanceSquared <= 1e-12 ||
    distanceSquared > CORNER_SNAP_TOLERANCE ** 2
  ) {
    return false;
  }
  const midpoint = sourcePoint.clone().add(targetPoint).multiplyScalar(0.5);
  sourcePoint.copy(midpoint);
  targetPoint.copy(midpoint);
  return true;
};

const connectWallEndpoints = (
  source: WallType,
  sourceEndpoint: WallEndpoint,
  target: WallType,
  targetEndpoint: WallEndpoint,
) => {
  const alreadyConnected = source.edgeConnections?.some(
    (connection) =>
      connection.targetWallId === target.id &&
      connection.sourceEndpoint === sourceEndpoint &&
      connection.targetEndpoint === targetEndpoint,
  );
  if (alreadyConnected) return;

  const connection = createEdgeConnection(
    source,
    sourceEndpoint,
    target,
    targetEndpoint,
  );
  source.edgeConnections!.push(connection);
  target.edgeConnections!.push({
    id: source.id,
    sourceWallId: target.id,
    targetWallId: source.id,
    sourceEndpoint: targetEndpoint,
    targetEndpoint: sourceEndpoint,
    sourceSide: connection.targetSide,
    targetSide: connection.sourceSide,
    sourceEdgePoint: connection.targetEdgePoint.clone(),
    targetEdgePoint: connection.sourceEdgePoint.clone(),
    gapDistance: connection.gapDistance,
    connectionAngle: connection.connectionAngle,
    voidFill: connection.voidFill ? { ...connection.voidFill } : undefined,
  });
};

const connectEndpointToWallMiddle = ({
  source,
  sourcePoint,
  sourceEndpoint,
  target,
}: {
  source: WallType;
  sourcePoint: THREE.Vector2;
  sourceEndpoint: WallEndpoint;
  target: WallType;
}) => {
  const targetVector = target.end.clone().sub(target.start);
  const targetLengthSquared = targetVector.lengthSq();
  if (targetLengthSquared <= 1e-12) return;

  const sourceVector = source.end.clone().sub(source.start);
  if (sourceVector.lengthSq() <= 1e-12) return;
  const directionSimilarity = Math.abs(
    sourceVector.normalize().dot(targetVector.clone().normalize()),
  );
  if (directionSimilarity >= 0.999) return;

  const projection = sourcePoint.clone().sub(target.start).dot(targetVector);
  const targetRatio = projection / targetLengthSquared;
  const endpointMargin = Math.min(
    0.1,
    T_JUNCTION_TOLERANCE / Math.sqrt(targetLengthSquared),
  );
  if (targetRatio <= endpointMargin || targetRatio >= 1 - endpointMargin) {
    return;
  }

  const projectedPoint = target.start
    .clone()
    .add(targetVector.multiplyScalar(targetRatio));
  if (
    sourcePoint.distanceToSquared(projectedPoint) >
    T_JUNCTION_TOLERANCE ** 2
  ) {
    return;
  }

  // Close the few-mm shortfall so the wall visually meets the target.
  sourcePoint.copy(projectedPoint);

  const sourceHasConnection = source.edgeConnections?.some(
    (connection) =>
      connection.targetWallId === target.id &&
      connection.sourceEndpoint === sourceEndpoint &&
      connection.targetEndpoint === "middle",
  );
  if (!sourceHasConnection) {
    source.edgeConnections!.push(
      createEdgeConnection(
        source,
        sourceEndpoint,
        target,
        "middle",
        projectedPoint,
      ),
    );
  }
};

const hasNoVisibleMeasurementEvidence = (draft: FloorplanImportDraft) =>
  draft.issues.some((issue) => {
    const code = issue.code.toUpperCase();
    const message = issue.message.toLowerCase();
    return (
      code === "NO_VISIBLE_MEASUREMENTS" ||
      /no (clear |visible )?(dimension|measurement)/.test(message) ||
      /(dimension|measurement).*(not visible|not present)/.test(message)
    );
  });

const getDraftBounds = (draft: FloorplanImportDraft) => {
  const points: Array<{ x: number; y: number }> = [];
  draft.walls.forEach((wall) => points.push(wall.start, wall.end));
  draft.lines.forEach((line) => points.push(line.start, line.end));
  draft.measurements.forEach((line) => points.push(line.start, line.end));
  draft.symbols.forEach((symbol) => points.push(symbol.position));
  draft.labels.forEach((label) => points.push(label.position));
  if (!points.length) {
    return {
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
      center: new THREE.Vector2(),
    };
  }
  const bounds = points.reduce(
    (result, point) => ({
      minX: Math.min(result.minX, point.x),
      maxX: Math.max(result.maxX, point.x),
      minY: Math.min(result.minY, point.y),
      maxY: Math.max(result.maxY, point.y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
  return {
    ...bounds,
    center: new THREE.Vector2(
      (bounds.minX + bounds.maxX) / 2,
      (bounds.minY + bounds.maxY) / 2,
    ),
  };
};

/**
 * Draft world origin (0,0) is the bottom-left of the source image after
 * Y-flip. Image center in meters is half the scaled source width/height.
 */
export const getImportImageCenterMeters = (draft: FloorplanImportDraft) =>
  new THREE.Vector2(
    (draft.sourceWidth * draft.scaleMmPerPixel) / 2000,
    (draft.sourceHeight * draft.scaleMmPerPixel) / 2000,
  );

const getAuthToken = () =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith("auth_token="))
    ?.split("=")
    .slice(1)
    .join("=");

/**
 * Copies the auth'd import source into durable floorplan backdrop storage so
 * the reference photo survives after the import job preview URL expires.
 */
export const uploadImportSourceAsBackdrop = async (
  sourceImageUrl: string,
): Promise<string> => {
  const headers: HeadersInit = {};
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${decodeURIComponent(token)}`;
  }
  const response = await fetch(sourceImageUrl, {
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    throw new Error("Could not load the reference image for the background.");
  }
  const blob = await response.blob();
  const mime = blob.type || "image/jpeg";
  const extension = mime.includes("png")
    ? "png"
    : mime.includes("webp")
      ? "webp"
      : "jpg";
  const file = new File([blob], `traced-reference-${Date.now()}.${extension}`, {
    type: mime,
  });

  const client = floorplannerStore.client ?? editorStore.client;
  if (!client) {
    throw new Error("Apollo client not initialized");
  }
  const floorplanId = await floorplannerStore.ensureCloudFloorplanId();
  const uploadResult = await client.mutate({
    mutation: UploadFloorplanBackdropImageDocument,
    variables: { floorplanId, image: file },
  });
  const imageUrl = uploadResult.data?.uploadFloorplanBackdropImage as
    | string
    | undefined;
  if (!imageUrl) {
    throw new Error("Could not save the reference image as a background.");
  }
  return imageUrl;
};

const createTextSymbol = (
  id: string,
  text: string,
  position: THREE.Vector2,
  zIndex: number,
): TextType => ({
  id,
  type: "text",
  name: text,
  text,
  position,
  zIndex,
  rotation: 0.0001,
  selected: false,
  lineColor: 0x000000,
  fillColor: 0xffffff,
  hoverColor: 0x0000ff,
  textBoxWidth: 1,
  textBoxHeight: 1,
  fontSize: 0.16,
  fontStyle: "normal",
  fontWeight: "normal",
  fontFamily: "sans-serif",
  textAlign: "center",
  lineHeight: 1,
});

export const compileFloorplanImportDraft = ({
  draft,
  excludedRefs,
  symbolOverrides = new Map(),
  copyIndex = 0,
  backdropImagePath,
}: {
  draft: FloorplanImportDraft;
  excludedRefs: ReadonlySet<string>;
  symbolOverrides?: ReadonlyMap<string, FloorplanImportSymbolOverride>;
  copyIndex?: number;
  /** Durable CDN URL for the reference photo placed under the traced walls. */
  backdropImagePath?: string;
}) => {
  const [visibleCenterX, visibleCenterY] = editorStore.getVisibleCenter();
  const draftBounds = getDraftBounds(draft);
  const copyGap = 1;
  const copyOffsetX =
    Math.max(0, copyIndex) * (draftBounds.maxX - draftBounds.minX + copyGap);
  // Prefer the viewport center (plus a lateral offset for re-inserting the same
  // scan). Into a non-empty plan, spiral-search so we do not land on existing work.
  const preferredCenter = new THREE.Vector2(
    visibleCenterX + copyOffsetX,
    visibleCenterY,
  );
  const contentBounds = {
    min: new THREE.Vector2(draftBounds.minX, draftBounds.minY),
    max: new THREE.Vector2(draftBounds.maxX, draftBounds.maxY),
  };
  const targetCenter = floorplannerStore.hasFloorplanDrawableContent()
    ? editorStore.findOpenContentCenter(contentBounds, preferredCenter)
    : preferredCenter;
  const offset = targetCenter.clone().sub(draftBounds.center);
  const point = (value: { x: number; y: number }) =>
    new THREE.Vector2(value.x, value.y).add(offset);

  const wallIdByRef = new Map<string, string>();
  const walls: WallType[] = draft.walls
    .filter((wall) => !excludedRefs.has(wall.ref))
    .map((wall) => {
      const id = floorplannerStore.generateId();
      wallIdByRef.set(wall.ref, id);
      return {
        id,
        name: wall.ref,
        type: "wall",
        start: point(wall.start),
        end: point(wall.end),
        centerConnections: [],
        edgeConnections: [],
        symbolAttachments: [],
        lineType: LineType.solid,
        lineWeight: floorplannerStore.wallLineWeight,
        wallWidth: Math.max(0.02, Math.min(1, wall.thickness)),
        lineColor: floorplannerStore.lineColor,
        fillColor: floorplannerStore.fillColor,
        hoverColor: floorplannerStore.hoverColor,
        lineForm: "straight",
        selectable: true,
        selected: false,
        threeD: { height: wall.height || 2.4 },
      };
    });

  for (let index = 0; index < walls.length; index += 1) {
    for (
      let otherIndex = index + 1;
      otherIndex < walls.length;
      otherIndex += 1
    ) {
      const source = walls[index];
      const target = walls[otherIndex];
      const endpointPairs: Array<[WallEndpoint, WallEndpoint]> = [
        ["start", "start"],
        ["start", "end"],
        ["end", "start"],
        ["end", "end"],
      ];
      const matches: Array<[WallEndpoint, WallEndpoint]> = [];
      endpointPairs.forEach(([sourceEndpoint, targetEndpoint]) => {
        snapCornerEndpoints(source, sourceEndpoint, target, targetEndpoint);
        if (
          samePoint(
            getWallEndpoint(source, sourceEndpoint),
            getWallEndpoint(target, targetEndpoint),
          )
        ) {
          matches.push([sourceEndpoint, targetEndpoint]);
        }
      });
      matches.forEach(([sourceEndpoint, targetEndpoint]) => {
        connectWallEndpoints(
          source,
          sourceEndpoint,
          target,
          targetEndpoint,
        );
      });

      connectEndpointToWallMiddle({
        source,
        sourcePoint: source.start,
        sourceEndpoint: "start",
        target,
      });
      connectEndpointToWallMiddle({
        source,
        sourcePoint: source.end,
        sourceEndpoint: "end",
        target,
      });
      connectEndpointToWallMiddle({
        source: target,
        sourcePoint: target.start,
        sourceEndpoint: "start",
        target: source,
      });
      connectEndpointToWallMiddle({
        source: target,
        sourcePoint: target.end,
        sourceEndpoint: "end",
        target: source,
      });
    }
  }

  const wallById = new Map(walls.map((wall) => [wall.id, wall]));
  const symbols: SymbolType[] = [];
  let zIndex = Math.max(
    0,
    ...Array.from(floorplannerStore.symbolsMap.values()).map(
      (symbol) => symbol.zIndex || 0,
    ),
  );

  draft.openings
    .filter(
      (opening) =>
        !excludedRefs.has(opening.ref) && !excludedRefs.has(opening.wallRef),
    )
    .forEach((opening) => {
      const wallId = wallIdByRef.get(opening.wallRef);
      const wall = wallId ? wallById.get(wallId) : undefined;
      if (!wall) return;
      const wallDirection = wall.end.clone().sub(wall.start).normalize();
      const id = floorplannerStore.generateId();
      const type =
        opening.kind === "DOUBLE_DOOR"
          ? "doubleDoor"
          : opening.kind === "WINDOW"
            ? "window"
            : opening.kind === "VOID"
              ? "void"
              : "door";
      const frameWidth = 0;
      // Traced openings are the visible gap users measure — use as BREDD directly
      // instead of subtracting door frames (that made 900mm show as ~700–800mm).
      const doorWidth = Math.max(0.01, opening.width);
      const doubleDoorWidth = Math.max(0.01, opening.width / 2);
      const centerOffset =
        type === "door"
          ? doorWidth / 2
          : type === "doubleDoor"
            ? doubleDoorWidth
            : 0;
      const anchorDistance = Math.max(0, opening.offset - centerOffset);
      const position = wall.start
        .clone()
        .add(wallDirection.clone().multiplyScalar(anchorDistance));
      const base: SymbolType = {
        id,
        type,
        name: opening.ref,
        position,
        zIndex: ++zIndex,
        rotation: Math.atan2(wallDirection.y, wallDirection.x),
        selected: false,
        attachedTo: wall.id,
        attachedDistance: anchorDistance,
        lineColor: floorplannerStore.lineColor,
        fillColor: floorplannerStore.fillColor,
        hoverColor: floorplannerStore.hoverColor,
        lineWeight:
          type === "window" || type === "void"
            ? floorplannerStore.windowLineWeight
            : floorplannerStore.doorLineWeight,
        threeD:
          type === "window"
            ? { height: 1.2, baseElevation: 0.9 }
            : type === "void"
              ? { height: wall.threeD?.height || 2.4, baseElevation: 0 }
              : { height: 2.1, baseElevation: 0 },
      };
      const symbol =
        type === "doubleDoor"
          ? {
              ...base,
              doubleDoorWidth,
              openAngle: floorplannerStore.openAngle,
              doorFrameWidth: frameWidth,
              doorBladeThickness: floorplannerStore.doorBladeThickness,
            }
          : type === "window"
            ? {
                ...base,
                windowWidth: floorplannerStore.windowWidth,
                windowLength: opening.width,
                windowFrameWidth: floorplannerStore.windowFrameWidth,
              }
            : type === "void"
              ? {
                  ...base,
                  voidWidth: Math.min(
                    wall.wallWidth || floorplannerStore.windowWidth,
                    floorplannerStore.windowWidth,
                  ),
                  voidLength: opening.width,
                }
              : {
                  ...base,
                  doorWidth,
                  openAngle: floorplannerStore.openAngle,
                  doorFrameWidth: frameWidth,
                  doorBladeThickness: floorplannerStore.doorBladeThickness,
                };
      symbols.push(symbol as SymbolType);
      wall.symbolAttachments!.push({
        symbolId: id,
        positionFromStart: anchorDistance,
      });
    });

  draft.symbols
    .filter((symbol) => !excludedRefs.has(symbol.ref))
    .forEach((symbol) => {
      const override = symbolOverrides.get(symbol.ref);
      const libraryKey = override?.libraryKey || symbol.libraryKey;
      const fallback = libraryKey
        ? undefined
        : getImportedRectangleFallback(symbol.semanticType);
      if (fallback) {
        const footprint = getImportedSymbolFootprint(symbol);
        symbols.push({
          id: floorplannerStore.generateId(),
          type: "square",
          name: symbol.semanticType,
          position: point(symbol.position),
          zIndex: ++zIndex,
          rotation: symbol.rotation ?? 0.0001,
          selected: false,
          squareWidth: footprint.width,
          squareHeight: footprint.height,
          openAngle: floorplannerStore.openAngle,
          lockAspectRatio: true,
          lineWeight: floorplannerStore.symbolLineWeight,
          lineColor: floorplannerStore.lineColor,
          fillColor: floorplannerStore.fillColor,
          hoverColor: floorplannerStore.hoverColor,
          threeD: {
            sourceMode: "primitiveOnly",
            shapeKind: "square",
            placementMode: "floor",
            height: fallback.height,
            baseElevation: fallback.baseElevation,
            collision: "box",
          },
        } as SquareSymbolType);
        return;
      }
      if (!libraryKey) return;
      const asset = resolveSymbolLibraryAsset(libraryKey);
      if (!asset || asset.legacyType !== "SvgSymbol") return;
      const assetWidth = asset.svgOptions?.svgLength || symbol.width || 1;
      const assetHeight = asset.svgOptions?.svgHeight || symbol.height || 1;
      symbols.push({
        id: floorplannerStore.generateId(),
        type: "svg",
        name: asset.label || symbol.semanticType,
        position: point(symbol.position),
        zIndex: ++zIndex,
        rotation:
          override?.rotation ??
          resolveImportedCatalogRotation({
            symbol,
            assetWidth,
            assetHeight,
          }),
        selected: false,
        libraryKey: asset.id,
        svgPath: asset.svgOptions?.svgPath || asset.src,
        svgLength: assetWidth,
        svgHeight: assetHeight,
        lockAspectRatio: true,
        lineWeight: floorplannerStore.symbolLineWeight,
        lineColor: floorplannerStore.lineColor,
        fillColor: floorplannerStore.fillColor,
        hoverColor: floorplannerStore.hoverColor,
        threeD: asset.svgOptions?.threeD,
      } as SvgType);
    });

  draft.labels
    .filter((label) => !excludedRefs.has(label.ref) && label.text.trim())
    .forEach((label) => {
      symbols.push(
        createTextSymbol(
          floorplannerStore.generateId(),
          label.text.trim(),
          point(label.position),
          ++zIndex,
        ),
      );
    });

  const lines: SingleLineType[] = draft.lines
    .filter((line) => !excludedRefs.has(line.ref))
    .map((line) => ({
      id: floorplannerStore.generateId(),
      name: line.ref,
      type: "singleLine",
      start: point(line.start),
      end: point(line.end),
      centerConnections: [],
      lineType: LineType.solid,
      lineWeight: floorplannerStore.singleLineWeight,
      lineColor: floorplannerStore.lineColor,
      fillColor: floorplannerStore.fillColor,
      hoverColor: floorplannerStore.hoverColor,
      lineForm: "straight",
      selectable: true,
      selected: false,
      zIndex: ++zIndex,
    }));

  const measurementsHaveVisibleEvidence =
    !hasNoVisibleMeasurementEvidence(draft);
  const rulers: RulerLineType[] = draft.measurements
    .filter((measurement) => {
      if (
        !measurementsHaveVisibleEvidence ||
        excludedRefs.has(measurement.ref) ||
        measurement.confidence < 0.75
      ) {
        return false;
      }
      const geometryMm =
        Math.hypot(
          measurement.end.x - measurement.start.x,
          measurement.end.y - measurement.start.y,
        ) * 1000;
      const relativeError =
        Math.abs(geometryMm - measurement.valueMm) /
        Math.max(geometryMm, measurement.valueMm, 1);
      return relativeError <= 0.2;
    })
    .map((measurement) => ({
      id: floorplannerStore.generateId(),
      name: `${Math.round(measurement.valueMm)} mm`,
      type: "rulerLine",
      start: point(measurement.start),
      end: point(measurement.end),
      centerConnections: [],
      lineType: LineType.solid,
      lineWeight: floorplannerStore.rulerLineWeight,
      lineColor: floorplannerStore.lineColor,
      fillColor: floorplannerStore.fillColor,
      hoverColor: floorplannerStore.hoverColor,
      lineForm: "straight",
      selectable: true,
      selected: false,
    }));

  if (
    backdropImagePath &&
    Number.isFinite(draft.sourceWidth) &&
    draft.sourceWidth > 0 &&
    Number.isFinite(draft.sourceHeight) &&
    draft.sourceHeight > 0 &&
    Number.isFinite(draft.scaleMmPerPixel) &&
    draft.scaleMmPerPixel > 0
  ) {
    const backdrop: BackdropImageType = {
      id: floorplannerStore.generateId(),
      type: "backdropImage",
      name: "Reference image",
      position: point(getImportImageCenterMeters(draft)),
      zIndex: -1,
      rotation: 0.0001,
      selected: false,
      lineColor: floorplannerStore.lineColor,
      fillColor: floorplannerStore.fillColor,
      hoverColor: floorplannerStore.hoverColor,
      lineWeight: floorplannerStore.symbolLineWeight,
      backdropImagePath,
      backdropRatio: 1,
      // Full source-image width in mm so the photo lines up with traced walls.
      backdropSize: draft.sourceWidth * draft.scaleMmPerPixel,
      // Unlocked so the user can select and move the background separately
      // from the traced drawing group.
      locked: false,
    };
    symbols.unshift(backdrop);
  }

  return { walls, symbols, lines, rulers };
};

export const IMPORT_BACKDROP_WARNING =
  "The drawing was inserted without the reference background. You can add the image from Uploads.";

type InsertedImportObject = WallType | SymbolType | SingleLineType | RulerLineType;

/**
 * Select the inserted drawing (group when present) and pan/zoom the 2D camera
 * so the import is clearly in focus instead of whatever was selected before.
 */
const focusImportedDrawing = (
  inserted: InsertedImportObject[],
  groupId?: string,
) => {
  editorStore.clearSelections();
  floorplannerStore.unSelectAll();

  const focusObjects: InsertedImportObject[] = groupId
    ? (floorplannerStore.getGroupObjects(groupId) as InsertedImportObject[])
    : inserted.filter(
        (object) => !(isSymbolType(object) && isBackdropImageType(object)),
      );

  if (focusObjects.length === 0) {
    return;
  }

  focusObjects.forEach((object) => {
    if (isWallType(object)) {
      floorplannerStore.selectWall(object.id);
    } else if (isSymbolType(object)) {
      floorplannerStore.selectSymbol(object.id);
    } else if (isSingleLineType(object)) {
      floorplannerStore.selectLine(object.id);
    } else if (isRulerLineType(object)) {
      floorplannerStore.selectRuler(object.id);
    }
  });

  // Seed editor selections; addSelection expands group membership when grouped.
  editorStore.addSelection(focusObjects[0] as FloorplannerSelectionType);
  if (!groupId) {
    focusObjects.slice(1).forEach((object) => {
      editorStore.addSelection(object as FloorplannerSelectionType);
    });
  }

  if (editorStore.view3D || !editorStore.camera) {
    return;
  }

  const bounds = getSelectionBounds(
    focusObjects.filter(isSelectionBoundsItem),
    { padding: 0.4 },
  );
  if (!bounds || !(bounds.width > 0) || !(bounds.height > 0)) {
    return;
  }

  const camera = editorStore.camera;
  camera.position.set(bounds.center.x, bounds.center.y, camera.position.z);

  if (camera instanceof THREE.PerspectiveCamera) {
    const fov = camera.fov * (Math.PI / 180);
    const distance = Math.abs(camera.position.z - 0) || 20;
    const viewportHeight = 2 * distance * Math.tan(fov / 2);
    const viewportWidth = viewportHeight * camera.aspect;
    const padding = 0.65;
    const zoomX = (viewportWidth * padding) / bounds.width;
    const zoomY = (viewportHeight * padding) / bounds.height;
    const nextZoom = Math.max(0.01, Math.min(100, Math.min(zoomX, zoomY)));
    editorStore.setZoomLevel(nextZoom);
  } else if (camera instanceof THREE.OrthographicCamera) {
    const visibleWidth = 2 * Math.abs(camera.right);
    const visibleHeight = 2 * Math.abs(camera.top);
    const padding = 0.65;
    const zoomX = (visibleWidth * padding) / bounds.width;
    const zoomY = (visibleHeight * padding) / bounds.height;
    const nextZoom = Math.max(0.01, Math.min(100, Math.min(zoomX, zoomY)));
    editorStore.setZoomLevel(nextZoom);
  }

  camera.updateProjectionMatrix();
};

export const applyFloorplanImportDraft = async ({
  draft,
  excludedRefs,
  symbolOverrides,
  importId,
  sourceImageUrl,
}: {
  draft: FloorplanImportDraft;
  excludedRefs: ReadonlySet<string>;
  symbolOverrides?: ReadonlyMap<string, FloorplanImportSymbolOverride>;
  importId: string;
  /** Auth'd import preview/source URL to persist as a background image. */
  sourceImageUrl?: string;
}) => {
  /**
   * Persist the source photo as a backdrop when possible. A failed upload
   * must not block inserting the reviewed walls and symbols.
   */
  let backdropImagePath: string | undefined;
  let backdropWarning: string | undefined;
  if (sourceImageUrl) {
    try {
      backdropImagePath = await uploadImportSourceAsBackdrop(sourceImageUrl);
    } catch (error) {
      console.warn(
        "Could not save the import reference image as a background",
        error,
      );
      backdropWarning = IMPORT_BACKDROP_WARNING;
    }
  }
  const copyIndex = floorplannerStore.getFloorplanImportCopyIndex(importId);
  const batch = compileFloorplanImportDraft({
    draft,
    excludedRefs,
    symbolOverrides,
    copyIndex,
    backdropImagePath,
  });
  await renderStore.prepareWallUpdateManager();
  const result = floorplannerStore.applyFloorplanImportBatch({
    ...batch,
    sourceImportId: importId,
  });
  const inserted = result.objectIds
    .map(
      (id) =>
        floorplannerStore.wallsMap.get(id) ||
        floorplannerStore.symbolsMap.get(id) ||
        floorplannerStore.singleLinesMap.get(id) ||
        floorplannerStore.rulerLinesMap.get(id),
    )
    .filter(Boolean) as InsertedImportObject[];
  focusImportedDrawing(inserted, result.groupId);
  invalidate();
  return { ...result, backdropWarning };
};
